import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { HeaderMainNav } from 'Src/components/navigation/HeaderMainNav';
import { TableRow } from 'Src/feature/data-table/TableRow';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import useRoute from 'Src/routes/useRoute';

import { EditTipModal } from './EditTipModal';
import { Field, Provider, useCheckListEditTips } from './useCheckListEditTips';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sectionId = 'CheckListEditTips';

const CheckListEditTips: FC = () => {
  const { t } = useTranslation();
  const { navigate } = useRoute();
  const store = useCheckListEditTips();
  const { Table, Column } = store.table;

  return (
    <>
      <Page
        main={(
          <PageMain
            header={(
              <PageMainHeader
                start={(
                  <>
                    <CloseIconButton onClick={() => navigate(-1)} />
                    <Copy variant="h6" value={t('check.labels.editTips')} />
                  </>
                )}
                end={<HeaderMainNav />}
              />
            )}
            main={(
              <Provider usedForm={store.filterForm} size="md">
                <Flex fullHeight>
                  <Flex horizontal fullWidth gap="sm" alignItems="center">
                    <Field
                      name="search"
                      type="search"
                      fullWidth
                      onAfterChange={() => store.onSearch()}
                    />
                    <Field
                      type="select"
                      name="checkStatus"
                      fullWidth
                      // variant="outlined"
                      onAfterChange={({ name, value }) => store.onAfterChange(name, value)}
                      options={store.options.status.map((opt) => ({ ...opt, labelProps: { line: { type: 'single' } } }))}
                    />
                    <Field
                      name="revenueCenterId"
                      type="select"
                      fullWidth
                      options={store.options.revenueCenterId}
                      onAfterChange={({ name, value }) => store.onAfterChange(name, value)}
                    />
                    <Field
                      name="employeeId"
                      type="select"
                      fullWidth
                      options={store.options.employeeId}
                      onAfterChange={({ name, value }) => store.onAfterChange(name, value)}
                    />
                  </Flex>
                  <Table
                    pagination={<></>}
                    noDataMessage={t('payment.error.adjustables_noData')}
                    renderer={{
                      row: (dataItem) => ({ children }) => (
                        <TableRow
                          onClick={() => {
                            store.tipAdjustForm.action.reset({
                              ...dataItem,
                              tipAmount: dataItem?.tipAmount?.toFixed(2) ?? '0',
                            });
                            store.modal.editTip.open();
                          }}
                        >
                          {children}
                        </TableRow>
                      )
                    }}
                  >
                    <Column label={t('check.labels.checkNumber')} field="checkNumber" />
                    <Column label={t('labels.table')} field="tableName" />
                    <Column
                      label={t('labels.timeClosed')}
                      field="closedAt"
                      sortable
                      cellContentRenderer={({ dataItem: { closedAt } }) =>
                        <Copy optional value={closedAt ? DateTime.fromMillis(closedAt)?.toLocaleString(DateTime.TIME_SIMPLE) : undefined} />}
                    />
                    <Column
                      label={t('check.labels.checkStatus')}
                      field="checkStatus"
                      cellContentRenderer={({ dataItem }) => {
                        const color = dataItem.checkStatus === 'Opened' ? 'secondary' : undefined;
                        return <Copy optional value={dataItem.checkStatus} color={color} />;
                      }}
                    />
                    <Column
                      width={200}
                      label={t('labels.owner')}
                      field="employeeId"
                      cellContentRenderer={({ value }) => {
                        const employee = value ? store.employeeMap.data?.get(value) : undefined;
                        return (
                          <Copy
                            optional
                            value={employee ? t('labels.util.employeeFullName', employee) : undefined}
                          />
                        );
                        
                      }}
                    />
                    <Column label={t('labels.paymentCard')} field="paymentMarker" />
                    <Column
                      label={t('check.labels.checkDollarSign')}
                      field="checkTotal"
                      align="right"
                      cellContentRenderer={({ dataItem }) => <Copy type="currency" value={dataItem.checkTotal} />}
                    />
                    <Column
                      label={t('check.labels.paymentDollarSign')}
                      field="paymentAmount"
                      align="right"
                      cellContentRenderer={({ dataItem }) => <Copy type="currency" value={dataItem.paymentAmount} />}
                    />
                    <Column
                      field="tipAmount"
                      cellContentRenderer={({ dataItem }) => (
                        <Flex horizontal gap={1} alignItems="center" wrap grow>
                          <Copy optional ellipsis value={t('labels.tip')} />
                          <Copy value={t('labels.util.currency', { value: dataItem.tipAmount || 0 })} ellipsis disabled={Boolean(dataItem?.tipAmount === 0)} />
                        </Flex>
                      )}
                    />
                    <Column
                      label={t('check.labels.paymentTotal')}
                      field="paymentTotal"
                      align="right"
                      cellContentRenderer={({ dataItem }) => <Copy type="currency" value={dataItem.paymentTotal} />}
                    />
                  </Table>
                  <EditTipModal
                    isOpen={store.modal.editTip.isOpen}
                    form={store.tipAdjustForm}
                    onCancel={() => {
                      store.modal.editTip.close();
                    }}
                  />
                </Flex>
              </Provider>
            )}
          />
        )}
      />
    </>
  );
};

export { CheckListEditTips };
